/**
 * The footer's destinations, and the version each published document carries (TROOP-203).
 *
 * **One list, read by the footer and by the router.** `Footer` renders a link per entry and
 * `App` maps the same entries to routes through a `Record<FooterPath, ReactNode>`, so a
 * destination added here without a screen behind it is a *compile* error rather than a link that
 * scrolls the page. That is the whole reason this file exists: `#privacy` and `#terms` were
 * anchors to nothing for a year — three of them, on every page in the product — and nothing in
 * the build, the tests or the browser said so. The list is not tested, deliberately: a test
 * asserting the list contains what the list contains is the recompute-what-you-test failure, and
 * the guarantee wanted here is the compiler's.
 *
 * **About is in the list even though it is not a legal document**, because the failure the list
 * prevents is about footer links rather than about legal ones, and a second mechanism beside this
 * one for the third link is how the third link drifts.
 */
export type FooterPath = '/about' | '/privacy' | '/terms';

export interface FooterLink {
  readonly path: FooterPath;
  readonly label: string;
}

/** Order is reading order: what the product is, then what it holds, then what it promises. */
export const FOOTER_LINKS: readonly FooterLink[] = [
  { path: '/about', label: 'About' },
  { path: '/privacy', label: 'Privacy' },
  { path: '/terms', label: 'Terms' },
];

/**
 * The published version of the privacy policy, shown on the page and meant to be quoted.
 *
 * **A date rather than a number**, so a parent asking "which one did I agree to" and a row in a
 * database are reading the same string. TROOP-204 records which version a guardian was shown when
 * they set their child's access up, and it references this constant rather than re-deriving a
 * version from a deployment date — a consent record pointing at a document nobody can identify is
 * not a consent record.
 *
 * **Bump it when the substance moves, never for a typo.** A version that changes on every commit
 * tells a parent nothing, and one that never changes is worse: the policy makes claims about what
 * the code does, so a change to what is collected, who may see it, or what is actually deleted
 * moves this string in the same commit that moves the code.
 */
export const PRIVACY_POLICY_VERSION = '2026-11-02';

/** The published version of the terms. Same rule as {@link PRIVACY_POLICY_VERSION}. */
export const TERMS_VERSION = '2026-09-14';
